import { useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { websocketService } from '../services/websocket';
import { statusFrom, toPositionUpdate } from '../services/droneService';
import {
  droneStatusChanged,
  fleetRefreshRequested,
  updateDronePosition,
} from '../store/slices/dronesSlice';
import {
  homeBaseUpdated,
  pathToHomeUpdated,
} from '../store/slices/homeBaseSlice';
import {
  setConnected,
  setDisconnected,
  incrementReconnectAttempts,
} from '../store/slices/connectionSlice';
import { missionDraftRemotelyUpdated } from '../store/slices/missionPlannerSlice';
import {
  geofenceBoundaryReported,
  lowBatteryLandingReported,
  droneCredentialsRevokedReported,
} from '../store/slices/safetySlice';
import { RootState } from '../store/store';
import { FleetEvent } from '../types/events';

/**
 * How long to wait before asking for the fleet again on behalf of the same
 * unknown drone. Telemetry arrives roughly once a second, so without a cooldown
 * a drone the backend does not yet list would trigger a refetch per message.
 */
const UNKNOWN_DRONE_REFETCH_COOLDOWN_MS = 15_000;

export const useWebSocket = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  // droneId -> when we last asked the fleet about it.
  const unknownDroneAskedAt = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    // Connect to the api-backend real-time gateway.
    void websocketService.connect();

    // Set up message handler
    const unsubscribe = websocketService.onMessage((event: FleetEvent) => {
      switch (event.type) {
        case 'drone_position_update': {
          // Telemetry for a drone this client has never seen means the fleet
          // snapshot is stale — it was enrolled, or came online, after mount.
          // updateDronePosition drops updates for unknown drones, so without
          // this the aircraft stays invisible until a page reload (#480).
          const update = toPositionUpdate(event.data);
          const known = Boolean(store.getState().drones.drones[update.droneId]);
          if (!known) {
            const now = Date.now();
            const askedAt = unknownDroneAskedAt.current.get(update.droneId);
            if (askedAt === undefined || now - askedAt > UNKNOWN_DRONE_REFETCH_COOLDOWN_MS) {
              unknownDroneAskedAt.current.set(update.droneId, now);
              dispatch(fleetRefreshRequested());
            }
          }
          dispatch(updateDronePosition(update));
          break;
        }
        // A drone fell silent (#787). Applied to every drone, not only the one
        // in view: a lost aircraft is the fleet-wide signal that matters most.
        case 'drone_status_changed':
          dispatch(
            droneStatusChanged({
              droneId: event.data.drone_id,
              status: statusFrom(event.data.status),
              lastUpdateTime: event.data.last_heartbeat,
            })
          );
          break;
        // Home base / path events carry a drone_id; only apply them when they
        // concern the drone currently in view, since the slice holds a single
        // drone's home base and path.
        case 'home_base_updated':
          if (
            event.data.drone_id === store.getState().drones.selectedDroneId
          ) {
            dispatch(homeBaseUpdated(event.data));
          }
          break;
        case 'path_to_home_updated':
          if (
            event.data.drone_id === store.getState().drones.selectedDroneId
          ) {
            dispatch(pathToHomeUpdated(event.data));
          }
          break;
        // A saved mission draft changed elsewhere. The slice ignores it unless
        // it concerns the draft this client currently has open (#321).
        case 'mission_draft_updated':
          dispatch(
            missionDraftRemotelyUpdated({
              id: event.data.id,
              revision: event.data.revision,
              deleted: event.data.deleted,
            })
          );
          break;
        // A drone reached its geofence and turned back (#452). Kept for every
        // drone, not only the one in view: a boundary event is a fleet-wide
        // safety signal, and filtering it to the selected drone would hide the
        // ones nobody happens to be looking at.
        case 'geofence_boundary':
          dispatch(geofenceBoundaryReported(event.data));
          break;
        // A drone is putting itself down on a low battery, and where (#629).
        // Kept for every drone, not only the one in view, for the same reason as
        // a boundary event: an aircraft descending somewhere its pilot did not
        // choose is a fleet-wide signal, and filtering it to the selected drone
        // would hide the ones nobody happens to be looking at.
        case 'low_battery_landing':
          dispatch(lowBatteryLandingReported(event.data));
          break;
        // A drone was cut off for a reused refresh token (#777, C8). Fleet-wide,
        // like the two above: the drone cannot report it, so this is the only
        // way its pilot hears.
        case 'drone_credentials_revoked':
          dispatch(droneCredentialsRevokedReported(event.data));
          break;
      }
    });

    // Monitor connection status
    const checkConnection = setInterval(() => {
      if (websocketService.isConnected()) {
        dispatch(setConnected());
      } else {
        dispatch(setDisconnected());
        dispatch(incrementReconnectAttempts());
      }
    }, 1000);

    // Cleanup
    return () => {
      unsubscribe();
      clearInterval(checkConnection);
      websocketService.disconnect();
    };
  }, [dispatch, store]);
};
